import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../services/accessControlService';

/**
 * Dev-mode gate for the integration diagnostics layer.
 *
 * devMode is on only when BOTH:
 *  - the signed-in user is authorized: an internal user (internal_users doc,
 *    userType 'internal') — or any user in a local development build; and
 *  - dev mode was requested: `?dev=1` in the URL or a prior toggle this
 *    session (`?dev=0` turns it off).
 *
 * Portal clients can never see it: the authorization check fails for them in
 * production regardless of URL params, so no dev component ever mounts.
 */

const STORAGE_KEY = 'bbaDevMode';
const authCache = new Map(); // uid -> boolean, one Firestore read per session

const readRequested = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const dev = params.get('dev');
    if (dev === '1') {
      sessionStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
    if (dev === '0') {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const useDevMode = () => {
  const { currentUser } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [requested, setRequested] = useState(readRequested);

  useEffect(() => {
    let cancelled = false;
    const uid = currentUser?.uid;
    if (!uid) {
      setAuthorized(false);
      return undefined;
    }
    if (process.env.NODE_ENV === 'development') {
      setAuthorized(true);
      return undefined;
    }
    if (authCache.has(uid)) {
      setAuthorized(authCache.get(uid));
      return undefined;
    }
    (async () => {
      try {
        const internal = await isInternalUser(uid);
        authCache.set(uid, !!internal);
        if (!cancelled) setAuthorized(!!internal);
      } catch (_) {
        if (!cancelled) setAuthorized(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const setDevMode = (on) => {
    try {
      if (on) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* ignore */ }
    setRequested(!!on);
  };

  return {
    devMode: authorized && requested,
    authorized,
    requested,
    setDevMode
  };
};

export default useDevMode;
